/**
 * STICK TO BOTTOM + "New messages ↓" — the Slack way (MODES.md §3):
 *  • reading at the bottom (within ``thresholdPx``) → a new message keeps you pinned to the newest;
 *  • scrolled up reading history → you're NOT yanked down; new arrivals count into a pill ("3 new messages");
 *  • my OWN message always brings me down (I just sent it);
 *  • clicking the pill — or scrolling back to the bottom yourself — clears it.
 * Only arrivals at the END count — an older page prepended by scroll-back is not "new".
 *
 *   const { newCount, jumpToBottom } = useStickToBottom({ rootRef, items, keyOf, isMine });
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export default function useStickToBottom({ rootRef, items = [], keyOf = (x) => x && x.id, isMine = () => false,
  thresholdPx = 80, resetKey = '' }) {
  const [newCount, setNewCount] = useState(0);
  const atBottom = useRef(true);
  const tail = useRef(null);

  const toBottom = useCallback((smooth = false) => {
    const root = rootRef.current;
    if (!root) return;
    if (smooth && typeof root.scrollTo === 'function') root.scrollTo({ top: root.scrollHeight, behavior: 'smooth' });
    else root.scrollTop = root.scrollHeight;
    atBottom.current = true;
    setNewCount(0);
  }, [rootRef]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onScroll = () => {
      atBottom.current = root.scrollHeight - root.scrollTop - root.clientHeight <= thresholdPx;
      if (atBottom.current) setNewCount(0);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [rootRef, thresholdPx]);

  // room switch: clear the pill and re-base the tail on what's shown now (NOT null — that would swallow the
  // first real arrival, since this runs after the layout effect has already recorded the tail)
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    const list = itemsRef.current || [];
    tail.current = list.length ? keyOf(list[list.length - 1]) : null;
    setNewCount(0);
    atBottom.current = true;
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const list = items || [];
    const last = list.length ? keyOf(list[list.length - 1]) : null;
    const prev = tail.current;
    tail.current = last;
    if (prev == null || last == null || last === prev) return;            // first load / nothing new at the end
    const at = list.findIndex((x) => keyOf(x) === prev);
    const arrivals = at >= 0 ? list.slice(at + 1) : list.slice(-1);
    if (!arrivals.length) return;
    if (atBottom.current || arrivals.some(isMine)) { toBottom(false); return; }
    setNewCount((n) => n + arrivals.length);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // LATE GROWTH (MODES.md §3, 2026-10-16): content that renders AFTER it lands — link cards, highlighted code, diagrams,
  // images, run history — grew the stream under a reader who opened at the bottom, leaving the NEWEST messages below
  // the fold ("the scroller hangs in the middle"). Watch the stream's height: while the reader is at the bottom, every
  // growth keeps them there. Growth is not a scroll, so ``atBottom`` keeps its last honest value — a reader scrolled
  // up (or a scroll-back page prepended above them) is never moved.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;
    let lastH = root.scrollHeight;
    const ro = new ResizeObserver(() => {
      const h = root.scrollHeight;
      const grew = h > lastH;
      lastH = h;
      if (grew && atBottom.current) root.scrollTop = root.scrollHeight;
    });
    const watch = () => { ro.disconnect(); ro.observe(root); Array.from(root.children || []).forEach((c) => ro.observe(c)); };
    watch();
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(watch) : null;
    if (mo) mo.observe(root, { childList: true });
    return () => { ro.disconnect(); if (mo) mo.disconnect(); };
  }, [rootRef, resetKey, (items || []).length > 0]);   // eslint-disable-line react-hooks/exhaustive-deps — re-arm once the stream mounts

  // FOLLOW (MODES.md §10.2a): content GROWING in place (an agent's answer streaming in) keeps a reader who is at the
  // bottom pinned there — never yanks someone reading history
  const follow = useCallback(() => { if (atBottom.current) toBottom(false); }, [toBottom]);
  return { newCount, jumpToBottom: () => toBottom(true), follow };
}
